import DisclosureToggle from 'tio-ui/components/disclosure-toggle';
import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import {
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhCheckCircle,
  PhWarning,
  PhTrash,
  PhEye,
  PhEyeSlash,
  PhArrowDown,
  PhArrowSquareOut,
} from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Callout from 'tio-ui/components/callout';
import { Section } from 'tio-ui/components/layout';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { and, not, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { openInNewTabs } from '../../../../../utils/open-in-new-tabs.ts';
import safeFormatDate from '../../../../../helpers/safe-format-date.ts';
import safeFormatNumber from '../../../../../helpers/safe-format-number.ts';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { AiRecommendationExtension as AiRecommendationModel } from 'tio-data/data/resources/ai-recommendation.ext';
import type { AssetSummaryExtension as AssetSummaryModel } from 'tio-data/data/resources/asset-summary.ext';
import type { AiValidationDisplayStatus } from 'tio-data/data/traits/digital-asset.ext';
import type { TOC } from '@ember/component/template-only';

const NotFound: TOC<{ Element: HTMLSpanElement }> = <template>
  <span class="tio-row-extra-tight app-icon-warning font-semibold" ...attributes>
    <PhWarning @size="16" @weight="fill" />
    {{t "common.tas.approval.drawer.attachments.analysis.not_found"}}
  </span>
</template>;

/**
 * Derived view-model for an attachment's categorized-upload validation.
 * `null` for attachments that never went through categorized upload (legacy or
 * free-form), which is how the chip/panel render nothing for them.
 */
interface AiValidationDisplay {
  status: AiValidationDisplayStatus;
  chipIntent: 'success' | 'warning' | 'danger' | 'default';
  // Terse status word for the at-a-glance card-header chip.
  chipLabel: string;
  // Fuller status for the detail-panel heading (e.g. "Flagged · Overridden").
  panelLabel: string;
  // True when the document carries something an approver should look at:
  // flagged / error / not_validated / accepted-override / type-mismatch / unverified identity.
  needsReview: boolean;
  panelTone: 'warning' | 'danger';
  feedback: string | null;
  typeMismatch: boolean;
  // Localized labels for the type-mismatch row (never raw DocumentType codes).
  selectedTypeLabel: string | null;
  detectedTypeLabel: string;
  identityIssue: boolean;
  overridden: boolean;
}

// Compact AI-validation status chip for an attachment card header. Renders
// nothing when the attachment did not go through categorized upload.
const AiStatusChip: TOC<{ Args: { v: AiValidationDisplay | null } }> = <template>
  {{#if @v}}
    <Chip @intent={{@v.chipIntent}} @size="sm" data-test-ai-validation-chip>
      {{@v.chipLabel}}
    </Chip>
  {{/if}}
</template>;

// Surfaces *why* an attachment needs review — AI feedback, a type mismatch, an
// unverified identity, or an employee override. Renders only when there is an
// actionable issue, so clean (or non-categorized) attachments show nothing.
const AiValidationPanel: TOC<{ Args: { v: AiValidationDisplay | null } }> = <template>
  {{#if @v}}
    {{#if @v.needsReview}}
      <div
        class="app-status-panel app-status-panel--{{@v.panelTone}} mb-4 p-3"
        data-test-ai-validation-panel
      >
        <h4 class="tio-row-tight tio-text-sm-semibold tio-fg-secondary mb-2">
          <PhWarning
            @size="16"
            @weight="fill"
            class={{if (eq @v.panelTone "danger") "app-icon-danger" "app-icon-warning"}}
          />
          {{@v.panelLabel}}
        </h4>

        {{#if (or (eq @v.status "pending") (eq @v.status "not_validated"))}}
          <p class="text-xs tio-fg-secondary mb-1">
            {{t "common.tas.approval.drawer.attachments.analysis.validation_unvalidated_note"}}
          </p>
        {{/if}}

        {{#if @v.feedback}}
          <p class="tio-row-extra-tight text-xs mb-1">
            <span class="font-semibold tio-fg-secondary">{{t
                "common.tas.approval.drawer.attachments.analysis.validation_label_ai_note"
              }}</span>
            <span class="tio-fg-secondary">{{@v.feedback}}</span>
          </p>
        {{/if}}

        {{#if @v.typeMismatch}}
          <p class="tio-row-extra-tight text-xs mb-1">
            <span class="font-semibold tio-fg-secondary">{{t
                "common.tas.approval.drawer.attachments.analysis.validation_label_type"
              }}</span>
            <span class="tio-fg-secondary">{{t
                "common.tas.attachments.ai.type_mismatch"
                selected=@v.selectedTypeLabel
                detected=@v.detectedTypeLabel
              }}</span>
          </p>
        {{/if}}

        {{#if @v.identityIssue}}
          <p class="tio-row-extra-tight text-xs mb-1">
            <span class="font-semibold tio-fg-secondary">{{t
                "common.tas.approval.drawer.attachments.analysis.validation_label_identity"
              }}</span>
            <span class="tio-fg-secondary">{{t
                "common.tas.approval.drawer.attachments.analysis.validation_identity"
              }}</span>
          </p>
        {{/if}}

        {{#if @v.overridden}}
          <p class="text-xs tio-fg-muted mt-2">
            {{t "common.tas.approval.drawer.attachments.analysis.validation_overridden"}}
          </p>
        {{/if}}
      </div>
    {{/if}}
  {{/if}}
</template>;

/**
 * Attachments Analysis View
 * Displays AI-generated document summaries for uploaded attachments
 * Now uses asset-summary as primary data source
 */
export interface AttachmentsAnalysisViewSignature {
  Args: {
    recommendation?: AiRecommendationModel;
    attachments: TasAssetModel[];
    assetSummaries?: AssetSummaryModel[];
    deleteAttachment?: {
      perform: (asset: TasAssetModel) => Promise<void>;
      isRunning: boolean;
    };
    isDeleted?: (asset: TasAssetModel) => boolean;
    // True only for TIO_DEVOPS users — unlocks the raw, un-deduped upload view.
    isDevOps?: boolean;
    // Shows the roster's "Open all" bulk-open button. Off by default; call
    // sites gate it to tio-admin approvers.
    showOpenAll?: boolean;
  };
}

interface TranscriptInfo {
  courses?: Array<{
    grade?: string;
    course?: string;
    period?: string;
    credits?: number;
    course_detail?: string;
  }>;
  program?: string;
  summary?: string;
  institution?: string;
  student_name?: string;
}

interface ReceiptInfo {
  entity?: string;
  summary?: string;
  line_items?: Array<{
    line_date?: string;
    line_item?: string;
    line_type?: string;
    line_amount?: number;
    line_details?: string;
  }>;
  payer_name?: string;
}

interface CertificateInfo {
  summary?: string;
  institution?: string;
  student_name?: string;
  completion_date?: string;
  degree_or_certification?: string;
  degreeOrCertification?: string;
  program?: string;
}

// Course syllabus / outline / learning plan. Mirrors `syllabus_schema` in
// tio-ai-agent (config/schemas.py) — the `is_required` flags are what let an
// approver check book/material costs against what the course actually requires.
interface SyllabusInfo {
  summary?: string;
  institution?: string;
  course_name?: string;
  course_start_date?: string;
  course_end_date?: string;
  textbooks?: Array<{
    title?: string;
    author?: string;
    edition?: string;
    isbn?: string;
    is_required?: boolean;
  }>;
  materials?: Array<{
    item?: string;
    is_required?: boolean;
  }>;
}

interface DocumentResult {
  general?: {
    summary: string;
  };
  transcript?: TranscriptInfo;
  receipt?: ReceiptInfo;
  certificate?: CertificateInfo;
  syllabus?: SyllabusInfo;
}

interface DocumentSummary {
  result?: DocumentResult;
  is_error?: boolean;
  file_name?: string;
}

class AttachmentsAnalysisView extends Component<AttachmentsAnalysisViewSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked attachmentToDelete: TasAssetModel | null = null;
  @tracked showConfirmModal = false;
  // Tracks which attachment summaries are expanded (collapsed by default)
  @tracked expandedSummaries = new Set<string>();
  // Tracks which discarded attachment cards are expanded (collapsed by default)
  @tracked expandedCards = new Set<string>();
  // DevOps-only: reveal every upload (id-deduped only), surfacing same-file
  // re-uploads and discarded records that the approver dedupe collapses.
  @tracked showAllUploads = false;

  // Deduplicate attachments by ID (the caller may pass the same record via multiple relationships)
  get uniqueAttachments(): TasAssetModel[] {
    const seenIds = new Set<string>();
    const deduped: TasAssetModel[] = [];
    for (const attachment of this.args.attachments) {
      if (attachment.id && !seenIds.has(attachment.id)) {
        seenIds.add(attachment.id);
        deduped.push(attachment);
      }
    }
    return deduped;
  }

  // Active (non-deleted) attachments — deduped by etag + filename so identical re-uploads
  // with the same name collapse, but same content with different filenames stay separate.
  // Prefers CARD_USAGE_PROOF classification, then most recent upload.
  get activeAttachments(): TasAssetModel[] {
    const { isDeleted } = this.args;
    const active = isDeleted
      ? this.uniqueAttachments.filter((a) => !isDeleted(a))
      : this.uniqueAttachments;

    return this.#dedupeByEtagAndFilename(active);
  }

  // Collapses identical re-uploads (same etag + filename), preferring
  // CARD_USAGE_PROOF then the most recent. Callers must filter out anything
  // they don't want BEFORE handing the list over: a discarded upload left in
  // can win its key and take the active copy's place.
  #dedupeByEtagAndFilename(attachments: TasAssetModel[]): TasAssetModel[] {
    const byKey = new Map<string, TasAssetModel>();
    for (const attachment of attachments) {
      const etag = attachment.assetSummary?.etag;
      // Dedup by etag + filename so identical re-uploads collapse,
      // but files with different names (same content) stay separate.
      const key = etag ? `etag:${etag}:${attachment.filename}` : `id:${attachment.id}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, attachment);
      } else {
        // Prefer CARD_USAGE_PROOF — it has special UI treatment (warning chip).
        const existingIsCardUsage = existing.classification === 'CARD_USAGE_PROOF';
        const currentIsCardUsage = attachment.classification === 'CARD_USAGE_PROOF';
        if (currentIsCardUsage && !existingIsCardUsage) {
          byKey.set(key, attachment);
        } else if (!currentIsCardUsage && existingIsCardUsage) {
          // keep existing
        } else {
          // Same priority — keep most recent
          const existingDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
          const currentDate = attachment.createdAt ? new Date(attachment.createdAt).getTime() : 0;
          if (currentDate > existingDate) {
            byKey.set(key, attachment);
          }
        }
      }
    }
    return [...byKey.values()];
  }

  // Discarded (deleted) attachments — shown individually (each deletion is a distinct event)
  get discardedAttachments(): TasAssetModel[] {
    const { isDeleted } = this.args;
    if (!isDeleted) return [];
    return this.uniqueAttachments.filter((a) => !!isDeleted(a));
  }

  // Sorted: active first, then discarded — single list for one loop
  get sortedAttachments(): TasAssetModel[] {
    return [...this.activeAttachments, ...this.discardedAttachments];
  }

  // --- Quick-view roster + DevOps raw view --------------------------------

  get isDevOps(): boolean {
    return this.args.isDevOps ?? false;
  }

  // Raw list for the DevOps view: id-deduped only (so same-file re-uploads each
  // remain), newest first, including discarded records.
  get rawAttachments(): TasAssetModel[] {
    return [...this.uniqueAttachments].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );
  }

  // The list the cards + roster iterate. The DevOps raw view overrides the
  // approver-facing dedupe; it can never engage for a non-DevOps user.
  get displayAttachments(): TasAssetModel[] {
    return this.showAllUploads && this.isDevOps ? this.rawAttachments : this.sortedAttachments;
  }

  // How many uploads the approver-facing dedupe (etag+filename) hides from view.
  get hiddenDuplicateCount(): number {
    return Math.max(0, this.uniqueAttachments.length - this.sortedAttachments.length);
  }

  // Roster order: documents that need review first, then the rest.
  get rosterAttachments(): TasAssetModel[] {
    const list = this.displayAttachments;
    const needs = list.filter((a) => this.aiValidationFor(a)?.needsReview);
    const rest = list.filter((a) => !this.aiValidationFor(a)?.needsReview);
    return [...needs, ...rest];
  }

  toggleShowAllUploads = () => {
    this.showAllUploads = !this.showAllUploads;
  };

  // Active attachments that can actually be opened — signedUrl is delivered in
  // the payload, but degrades to null (the roster then renders a plain span).
  // Discarded uploads are dropped BEFORE the dedupe rather than after. Filtering
  // afterwards loses documents: activeAttachments only honours the optional
  // @isDeleted arg, so without it a discarded upload can win its etag+filename
  // key, and removing it later takes the active copy that shared that key with
  // it. The opener owns this guarantee itself rather than inheriting it from an
  // arg a future call site could omit.
  get openableActiveAttachments(): TasAssetModel[] {
    const { isDeleted } = this.args;
    const notDiscarded = this.uniqueAttachments.filter(
      (a) => !a.attachmentDeletedAt && !isDeleted?.(a)
    );
    return this.#dedupeByEtagAndFilename(notDiscarded).filter((a) => !!a.signedUrl);
  }

  get showOpenAll(): boolean {
    return (this.args.showOpenAll ?? false) && this.openableActiveAttachments.length > 0;
  }

  // Bulk-open: one tab per active document. The batch must stay synchronous so
  // every window.open lands inside this click's user-gesture window; blocked
  // tabs can only be fixed by the user allowing pop-ups, so tell them.
  openAllActiveAttachments = () => {
    const urls: string[] = [];
    for (const attachment of this.openableActiveAttachments) {
      if (attachment.signedUrl) urls.push(attachment.signedUrl);
    }
    const { blocked } = openInNewTabs(urls);
    if (blocked > 0) {
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.analysis.open_in_tabs_popup_blocked', {
          count: blocked,
        }),
        { appearance: 'error' }
      );
    }
  };

  // Maps each rendered card's element by attachment id (populated by the
  // registerCard modifier) so the roster's jump-to-section can scroll to a card
  // without querying the document.
  #cardElements = new Map<string, HTMLElement>();

  registerCard = modifier((element: HTMLElement, [id]: [string | null | undefined]) => {
    if (!id) return undefined;
    this.#cardElements.set(id, element);
    return () => {
      this.#cardElements.delete(id);
    };
  });

  scrollToCard = (id: string | null | undefined) => {
    if (!id) return;
    this.#cardElements.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Human-readable label for the category an employee uploaded a document as
  // (categorized upload only). Null for legacy/free-form uploads, so the chip
  // simply doesn't render for them.
  #documentCategoryLabel(type: string | null): string | null {
    const prefix = 'common.tas.approval.drawer.attachments.analysis';
    switch (type) {
      case 'receipt':
        return this.intl.t(`${prefix}.category_receipt`);
      case 'transcript':
        return this.intl.t(`${prefix}.category_transcript`);
      case 'grade':
        return this.intl.t(`${prefix}.category_grade`);
      case 'certificate':
        return this.intl.t(`${prefix}.category_certificate`);
      case 'syllabus':
        return this.intl.t(`${prefix}.category_syllabus`);
      case 'general':
        return this.intl.t(`${prefix}.category_general`);
      default:
        return null;
    }
  }

  categoryFor = (attachment: TasAssetModel): string | null => {
    return this.#documentCategoryLabel(attachment.employeeSelectedType);
  };

  // Localized, comma-joined labels of the AI-detected types (for the type-mismatch
  // row), falling back to the raw code for any type without a localized label.
  #detectedTypesLabel(attachment: TasAssetModel): string {
    const types = attachment.aiDetectedTypes;
    if (!types.length) return this.#documentCategoryLabel('general') ?? 'general';
    return types.map((t) => this.#documentCategoryLabel(t) ?? t).join(', ');
  }

  // AI-inferred document type (the summary AI's heuristic) for attachments that
  // weren't categorized — surfaced as a clearly-distinguished "guess" chip.
  // Null when there's no summary to infer from.
  inferredTypeFor = (attachment: TasAssetModel): string | null => {
    const assetSummary = this.getAssetSummaryForAttachment(attachment);
    if (!assetSummary) return null;
    const docSummary = this.getDocumentSummaryFromAssetSummary(assetSummary);
    if (!docSummary) return null;
    return this.getDocumentTypeInfo(docSummary).label;
  };

  toggleCard = (id: string | null) => {
    if (!id) return;
    if (this.expandedCards.has(id)) {
      this.expandedCards.delete(id);
    } else {
      this.expandedCards.add(id);
    }
    this.expandedCards = new Set(this.expandedCards);
  };

  isCardExpanded = (id: string | null): boolean => {
    if (!id) return false;
    return this.expandedCards.has(id);
  };

  // Whether the card body should be visible — always true for active, requires expand for discarded
  isBodyVisible = (attachment: TasAssetModel): boolean => {
    if (!this.args.isDeleted || !this.args.isDeleted(attachment)) return true;
    return this.expandedCards.has(attachment.id ?? '');
  };

  isFirstDiscarded = (attachment: TasAssetModel): boolean => {
    const discarded = this.discardedAttachments;
    return discarded.length > 0 && discarded[0]?.id === attachment.id;
  };

  toggleSummary = (key: string | null) => {
    if (!key) return;
    if (this.expandedSummaries.has(key)) {
      this.expandedSummaries.delete(key);
    } else {
      this.expandedSummaries.add(key);
    }
    // Force reactivity by reassigning
    this.expandedSummaries = new Set(this.expandedSummaries);
  };

  isSummaryExpanded = (key: string | null): boolean => {
    if (!key) return false;
    return this.expandedSummaries.has(key);
  };

  confirmDelete = (attachment: TasAssetModel) => {
    this.attachmentToDelete = attachment;
    this.showConfirmModal = true;
  };

  cancelDelete = () => {
    this.attachmentToDelete = null;
    this.showConfirmModal = false;
  };

  performDelete = async () => {
    if (this.attachmentToDelete && this.args.deleteAttachment) {
      await this.args.deleteAttachment.perform(this.attachmentToDelete);
      this.attachmentToDelete = null;
      this.showConfirmModal = false;
    }
  };

  getAssetSummaryForAttachment = (attachment: TasAssetModel): AssetSummaryModel | null => {
    try {
      if (!attachment) {
        return null;
      }

      // First try to get from the attachment's direct relationship
      if (attachment.assetSummary) {
        return attachment.assetSummary;
      }

      // Fallback to searching in the passed array
      if (this.args.assetSummaries) {
        const summary = this.args.assetSummaries.find(
          (summary) => summary.digitalAsset?.id === attachment.id
        );
        return summary || null;
      }

      return null;
    } catch (error) {
      console.error('Error getting asset summary for attachment:', error);
      return null;
    }
  };

  /**
   * Extract document summary from asset summary
   * Asset summary.summary contains the full analysis including result, is_error, file_name
   */
  getDocumentSummaryFromAssetSummary = (
    assetSummary: AssetSummaryModel
  ): DocumentSummary | null => {
    try {
      if (!assetSummary) {
        return null;
      }

      if (!assetSummary.summary) {
        return null;
      }

      // The summary attribute contains the full DocumentSummary structure
      // Cast it to DocumentSummary type
      const summary = assetSummary.summary as unknown as DocumentSummary;

      return summary;
    } catch (error) {
      console.error('Error extracting document summary from asset summary:', error);
      return null;
    }
  };

  getFileIcon = (attachment: TasAssetModel) => {
    const extension = attachment.extension?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return PhFilePdf;
      case 'doc':
      case 'docx':
        return PhFileDoc;
      case 'xls':
      case 'xlsx':
        return PhFileXls;
      case 'ppt':
      case 'pptx':
        return PhFilePpt;
      case 'txt':
        return PhFileTxt;
      case 'zip':
        return PhFileZip;
      case 'jpg':
      case 'jpeg':
        return PhFileJpg;
      case 'png':
        return PhFilePng;
      case 'gif':
      case 'bmp':
      case 'svg':
        return PhFileImage;
      case 'csv':
        return PhFileCsv;
      case 'text':
        return PhFileText;
      default:
        return PhFile;
    }
  };

  /** Infers the document chip from structured AI data, then summary keywords. */
  getDocumentTypeInfo = (
    docSummary?: DocumentSummary
  ): { label: string; intent: 'success' | 'warning' | 'default' | 'primary' | 'danger' } => {
    const prefix = 'common.tas.approval.drawer.attachments.analysis';

    if (!docSummary?.result) {
      return { label: this.intl.t(`${prefix}.doc_type_unknown`), intent: 'default' };
    }

    if (docSummary.result.receipt) {
      return { label: this.intl.t(`${prefix}.doc_type_receipt`), intent: 'success' };
    }
    if (docSummary.result.transcript) {
      return { label: this.intl.t(`${prefix}.doc_type_transcript`), intent: 'primary' };
    }
    if (docSummary.result.syllabus) {
      return { label: this.intl.t(`${prefix}.doc_type_syllabus`), intent: 'default' };
    }

    // Older summaries lack structured document types, leaving summary keywords as the only signal.
    const summary = docSummary.result.general?.summary?.toLowerCase() || '';

    if (summary.includes('receipt') || summary.includes('invoice') || summary.includes('payment')) {
      return { label: this.intl.t(`${prefix}.doc_type_receipt`), intent: 'success' };
    }
    if (summary.includes('transcript')) {
      return { label: this.intl.t(`${prefix}.doc_type_transcript`), intent: 'primary' };
    }
    if (
      summary.includes('enrollment') ||
      summary.includes('enrolment') ||
      summary.includes('proof of enrollment') ||
      summary.includes('enrollment verification')
    ) {
      return { label: this.intl.t(`${prefix}.doc_type_enrollment`), intent: 'primary' };
    }
    if (summary.includes('grade report') || summary.includes('report card')) {
      return { label: this.intl.t(`${prefix}.doc_type_grade_report`), intent: 'primary' };
    }
    if (summary.includes('syllabus')) {
      return { label: this.intl.t(`${prefix}.doc_type_syllabus`), intent: 'default' };
    }
    if (
      summary.includes('completion') ||
      summary.includes('certificate') ||
      summary.includes('certification')
    ) {
      return { label: this.intl.t(`${prefix}.doc_type_completion`), intent: 'success' };
    }
    if (summary.includes('bill') || summary.includes('tuition statement')) {
      return { label: this.intl.t(`${prefix}.doc_type_tuition_bill`), intent: 'success' };
    }

    // Fallback to generic document
    return { label: this.intl.t(`${prefix}.doc_type_document`), intent: 'default' };
  };

  // ── Categorized-upload AI validation ──

  // Active attachments that went through categorized upload AND were actually
  // AI-analyzed. Skip-AI (general) docs are excluded so the "N AI-validated"
  // summary counts stay honest — they still render their neutral chip
  // per-attachment. Drives whether the AI-validation summary callout appears.
  get categorizedActiveAttachments(): TasAssetModel[] {
    return this.activeAttachments.filter(
      (a) => a.aiValidationDisplayStatus != null && !a.skipAiAnalysis
    );
  }

  // Categorized attachments an approver should look at before deciding.
  get attachmentsNeedingReview(): TasAssetModel[] {
    return this.categorizedActiveAttachments.filter((a) => this.aiValidationFor(a)?.needsReview);
  }

  get hasCategorizedUploads(): boolean {
    return this.categorizedActiveAttachments.length > 0;
  }

  // An identity check only counts as failed when the tag is explicitly "false";
  // a missing tag (legacy upload, or AI hasn't responded yet) is treated as
  // unknown so we don't raise a false-positive identity warning.
  #hasIdentityFailure(attachment: TasAssetModel): boolean {
    const raw =
      attachment.tags?.['aiIdentityMatch'] ??
      attachment.tags?.['ai_identity_match'] ??
      attachment.tags?.['ai-identity-match'];
    return raw === 'false';
  }

  // Builds the chip + review-panel view-model for one attachment, or null when
  // the attachment never went through categorized upload.
  aiValidationFor = (attachment: TasAssetModel): AiValidationDisplay | null => {
    const status = attachment.aiValidationDisplayStatus;
    if (!status) return null;

    // Skip-AI docs (general uploads) are stamped `valid` at upload without the
    // AI ever reading them — show an honest neutral "Not Analyzed" chip
    // instead of "AI Verified", and raise no review flags for them.
    if (attachment.skipAiAnalysis) {
      const notAnalyzedLabel = this.intl.t('common.tas.attachments.ai.not_analyzed');
      return {
        status,
        chipIntent: 'default',
        chipLabel: notAnalyzedLabel,
        panelLabel: notAnalyzedLabel,
        needsReview: false,
        panelTone: 'warning',
        feedback: null,
        typeMismatch: false,
        selectedTypeLabel:
          this.#documentCategoryLabel(attachment.employeeSelectedType) ??
          attachment.employeeSelectedType,
        detectedTypeLabel: this.#detectedTypesLabel(attachment),
        identityIssue: false,
        overridden: false,
      };
    }

    const selectedType = attachment.employeeSelectedType;
    const typeMismatch =
      !!selectedType &&
      selectedType !== 'general' &&
      attachment.aiDetectedTypes.length > 0 &&
      !attachment.aiTypeMatch;
    const identityIssue = this.#hasIdentityFailure(attachment);

    const isProblem = status === 'flagged' || status === 'error' || status === 'not_validated';
    // `pending` means the AI verdict never landed (orphaned at upload, since the
    // terminal state is resolved client-side) — surface it as needs-review so an
    // un-validated document isn't silently approved.
    const needsReview =
      isProblem || status === 'accepted' || status === 'pending' || typeMismatch || identityIssue;

    const chipIntent =
      status === 'valid'
        ? 'success'
        : status === 'flagged' || status === 'error'
          ? 'danger'
          : status === 'accepted' || status === 'not_validated' || status === 'pending'
            ? 'warning'
            : 'default';

    const panelTone = status === 'flagged' || status === 'error' ? 'danger' : 'warning';

    // Chip stays terse ("Overridden"); the panel heading carries the fuller
    // "Flagged · Overridden" so the at-a-glance chip isn't crowded.
    // An orphaned `pending` (verdict never persisted — see the back-end ticket) reads
    // to an approver exactly like `not_validated`: no usable AI verdict, review manually.
    // The real status stays in the data + DevOps raw view for debugging.
    const labelStatus = status === 'pending' ? 'not_validated' : status;
    const chipLabel = this.intl.t(`common.tas.attachments.ai.${labelStatus}`);
    const panelLabel =
      status === 'accepted'
        ? this.intl.t('common.tas.attachments.ai.flagged_overridden')
        : chipLabel;

    return {
      status,
      chipIntent,
      chipLabel,
      panelLabel,
      needsReview,
      panelTone,
      feedback: attachment.aiValidationFeedback,
      typeMismatch,
      // Raw `selectedType` stays for the mismatch logic above; the view-model
      // exposes only localized labels so internal codes never reach the UI.
      selectedTypeLabel: this.#documentCategoryLabel(selectedType) ?? selectedType,
      detectedTypeLabel: this.#detectedTypesLabel(attachment),
      identityIssue,
      overridden: status === 'accepted',
    };
  };

  <template>
    <div class="p-2 overflow-x-hidden">
      <h3 class="tio-text-base-semibold mb-4 tio-fg-primary">{{t
          "common.tas.approval.drawer.attachments.analysis.document_analysis"
        }}</h3>
      <p class="text-sm tio-fg-secondary mb-6">
        {{t "common.tas.approval.drawer.attachments.analysis.description"}}
      </p>

      {{#if this.hasCategorizedUploads}}
        {{#if this.attachmentsNeedingReview.length}}
          <Callout
            @intent="warning"
            @title={{t
              "common.tas.approval.drawer.attachments.analysis.validation_summary_title_issues"
              count=this.attachmentsNeedingReview.length
              total=this.categorizedActiveAttachments.length
            }}
            class="mb-6"
            data-test-ai-validation-summary
          >
            <:icon><PhWarning @size="20" @weight="fill" /></:icon>
            <:default>
              <p class="text-sm tio-fg-secondary">
                {{t
                  "common.tas.approval.drawer.attachments.analysis.validation_summary_body_issues"
                }}
              </p>
            </:default>
          </Callout>
        {{else}}
          <Callout @intent="success" @align="center" class="mb-6" data-test-ai-validation-summary>
            <:icon><PhCheckCircle @size="20" @weight="fill" /></:icon>
            <:default>
              <p class="text-sm tio-fg-secondary">
                {{t
                  "common.tas.approval.drawer.attachments.analysis.validation_summary_clear"
                  count=this.categorizedActiveAttachments.length
                }}
              </p>
            </:default>
          </Callout>
        {{/if}}
      {{/if}}

      {{#if this.rosterAttachments.length}}
        <div class="mb-6" data-test-attachment-roster>
          <div class="tio-row-between mb-2">
            <h4 class="tio-text-xs-semibold tio-fg-disabled uppercase tracking-wide">
              {{t
                "common.tas.approval.drawer.attachments.analysis.roster_title"
                count=this.rosterAttachments.length
              }}
            </h4>
            <div class="tio-row-tight">
              {{#if this.isDevOps}}
                <button
                  type="button"
                  class="tio-row-extra-tight text-xs tio-fg-secondary hover:tio-fg-primary"
                  aria-pressed={{if this.showAllUploads "true" "false"}}
                  {{on "click" this.toggleShowAllUploads}}
                  data-test-show-all-uploads
                >
                  {{#if this.showAllUploads}}
                    <PhEye @size="14" />
                    {{t "common.tas.approval.drawer.attachments.analysis.showing_all_uploads_raw"}}
                  {{else}}
                    <PhEyeSlash @size="14" />
                    {{t "common.tas.approval.drawer.attachments.analysis.show_all_uploads_raw"}}
                    {{#if this.hiddenDuplicateCount}}
                      <span class="tio-fg-muted">{{t
                          "common.tas.approval.drawer.attachments.analysis.duplicates_hidden"
                          count=this.hiddenDuplicateCount
                        }}</span>
                    {{/if}}
                  {{/if}}
                </button>
              {{/if}}
              {{#if this.showOpenAll}}
                <Button
                  @testHook="tas-attachments-analysis__open-all"
                  @intent="default"
                  @appearance="outlined"
                  @size="sm"
                  @icon={{PhArrowSquareOut}}
                  @iconSize={{14}}
                  @label={{t
                    "common.tas.approval.drawer.attachments.analysis.open_in_tabs"
                    count=this.openableActiveAttachments.length
                  }}
                  @ariaLabel={{t
                    "common.tas.approval.drawer.attachments.analysis.open_in_tabs_aria"
                    count=this.openableActiveAttachments.length
                  }}
                  class="text-xs"
                  {{on "click" this.openAllActiveAttachments}}
                  data-test-open-all-attachments-button
                />
              {{/if}}
            </div>
          </div>
          <ul class="rounded-lg border tio-border-default overflow-hidden tio-bg-surface">
            {{#each this.rosterAttachments as |attachment|}}
              <li
                class="tio-row-tight px-3 py-2 border-b tio-border-default last:border-b-0 text-sm"
                data-test-roster-row
              >
                {{#let (this.getFileIcon attachment) as |FileIcon|}}
                  <FileIcon @size="18" class="shrink-0 tio-fg-muted" />
                {{/let}}
                {{#if attachment.signedUrl}}
                  <a
                    href={{attachment.signedUrl}}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={{t
                      "common.tas.approval.drawer.attachments.analysis.view_file"
                      fileName=attachment.filename
                    }}
                    class="tio-fill-min truncate hover:underline
                      {{if (@isDeleted attachment) 'line-through tio-fg-muted' 'tio-fg-secondary'}}"
                  >{{attachment.filename}}</a>
                {{else}}
                  <span
                    class="tio-fill-min truncate
                      {{if (@isDeleted attachment) 'line-through tio-fg-muted' 'tio-fg-secondary'}}"
                  >{{attachment.filename}}</span>
                {{/if}}
                {{#let (this.categoryFor attachment) as |category|}}
                  {{#if category}}
                    <Chip @intent="default" @size="sm">{{category}}</Chip>
                  {{else}}
                    {{#let (this.inferredTypeFor attachment) as |inferred|}}
                      {{#if inferred}}
                        <Chip
                          @intent="default"
                          @appearance="outlined"
                          @size="sm"
                          title={{t
                            "common.tas.approval.drawer.attachments.analysis.inferred_type_title"
                          }}
                        >{{inferred}}</Chip>
                      {{/if}}
                    {{/let}}
                  {{/if}}
                {{/let}}
                <AiStatusChip @v={{this.aiValidationFor attachment}} />
                <span class="shrink-0 tio-text-caption">
                  {{safeFormatDate
                    attachment.createdAt
                    month="2-digit"
                    day="2-digit"
                    year="2-digit"
                  }}
                </span>
                <button
                  type="button"
                  class="shrink-0 p-1 tio-fg-muted hover:tio-fg-primary"
                  {{on "click" (fn this.scrollToCard attachment.id)}}
                  aria-label={{t
                    "common.tas.approval.drawer.attachments.analysis.jump_to_details"
                    fileName=attachment.filename
                  }}
                  title={{t
                    "common.tas.approval.drawer.attachments.analysis.jump_to_details"
                    fileName=attachment.filename
                  }}
                  data-test-jump-to-card
                >
                  <PhArrowDown @size="16" />
                </button>
              </li>
            {{/each}}
          </ul>
        </div>
      {{/if}}

      <div class="space-y-4">
        {{#if this.displayAttachments.length}}
          {{#each this.displayAttachments as |attachment|}}
            {{#if (and (not this.showAllUploads) (this.isFirstDiscarded attachment))}}
              <div class="mt-6 mb-3 border-t tio-border-default pt-4">
                <h4 class="tio-text-xs-semibold tio-fg-disabled uppercase tracking-wide">
                  {{t
                    "common.tas.approval.drawer.attachments.analysis.discarded"
                    count=this.discardedAttachments.length
                  }}
                </h4>
              </div>
            {{/if}}

            {{#let (this.getAssetSummaryForAttachment attachment) as |assetSummary|}}
              {{#if assetSummary}}
                {{#let (this.getDocumentSummaryFromAssetSummary assetSummary) as |docSummary|}}
                  {{#if docSummary}}
                    <Section {{this.registerCard attachment.id}}>
                      <:header>
                        <div class="tio-col-tight">
                          <div class="tio-row-tight w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="tio-col-extra-tight tio-fill-min">
                                <div class="tio-row-tight tio-fg-muted italic min-w-0">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="shrink-0" />
                                        <span class="block truncate">{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="shrink-0" />
                                      <span class="block truncate">{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                  <span class="shrink-0 text-sm">{{t
                                      "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                    }}</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs tio-fg-disabled ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <button
                                type="button"
                                aria-expanded={{if
                                  (this.isCardExpanded attachment.id)
                                  "true"
                                  "false"
                                }}
                                class="p-1 tio-fg-disabled hover:tio-fg-secondary transition-colors shrink-0"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.toggle_details"
                                }}
                                {{on "click" (fn this.toggleCard attachment.id)}}
                              >
                                <DisclosureToggle
                                  @expanded={{this.isCardExpanded attachment.id}}
                                  @bare={{true}}
                                />
                              </button>
                            {{else}}
                              <div class="tio-col-extra-tight tio-fill-min">
                                <div class="tio-row-tight min-w-0 overflow-hidden">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="tio-fg-brand shrink-0" />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="tio-fg-brand shrink-0" />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="tio-text-caption ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="tio-row-tight shrink-0 ml-auto">
                                <AiStatusChip @v={{this.aiValidationFor attachment}} />
                                {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                                  <Chip @intent="warning" @size="sm">{{t
                                      "common.tas.approval.drawer.attachments.card_usage_receipt"
                                    }}</Chip>
                                {{/if}}
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 tio-fg-danger hover:tio-bg-danger-soft rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    aria-label={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        {{#if (this.isBodyVisible attachment)}}
                          <div class="break-words">
                            <AiValidationPanel @v={{this.aiValidationFor attachment}} />

                            {{#if
                              (or
                                docSummary.result.receipt
                                docSummary.result.transcript
                                docSummary.result.certificate
                                docSummary.result.syllabus
                                docSummary.is_error
                              )
                            }}
                              {{#if docSummary.result.certificate}}
                                <div class="app-status-panel app-status-panel--success mb-4 p-3">
                                  <h4
                                    class="tio-row-tight tio-text-sm-semibold tio-fg-secondary mb-2"
                                  >
                                    <PhCheckCircle @size="16" class="app-icon-success" />
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.certificate_details"
                                      default="Certificate Details"
                                    }}
                                  </h4>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.completion_date"
                                        default="Completion Date:"
                                      }}</span>
                                    {{#if docSummary.result.certificate.completion_date}}
                                      <span class="tio-fg-secondary">{{safeFormatDate
                                          docSummary.result.certificate.completion_date
                                          month="short"
                                          day="numeric"
                                          year="numeric"
                                        }}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.credential"
                                        default="Credential:"
                                      }}</span>
                                    {{#if
                                      (or
                                        docSummary.result.certificate.degree_or_certification
                                        docSummary.result.certificate.degreeOrCertification
                                      )
                                    }}
                                      <span class="tio-fg-secondary">{{or
                                          docSummary.result.certificate.degree_or_certification
                                          docSummary.result.certificate.degreeOrCertification
                                        }}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.institution"
                                      }}</span>
                                    {{#if docSummary.result.certificate.institution}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.institution}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.student"
                                      }}</span>
                                    {{#if docSummary.result.certificate.student_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.student_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.program"
                                      }}</span>
                                    {{#if docSummary.result.certificate.program}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.program}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                </div>
                              {{/if}}

                              {{#if docSummary.result.receipt}}
                                <div
                                  class="mb-4 p-3 tio-bg-success-soft border tio-border-success rounded-lg"
                                >
                                  <h4
                                    class="tio-row-tight tio-text-sm-semibold tio-fg-secondary mb-2"
                                  >
                                    <PhCheckCircle @size="16" class="tio-fg-success" />
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.payment_details"
                                    }}
                                  </h4>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.entity"
                                      }}</span>
                                    {{#if docSummary.result.receipt.entity}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.receipt.entity}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.payer"
                                      }}</span>
                                    {{#if docSummary.result.receipt.payer_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.receipt.payer_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  {{#if docSummary.result.receipt.line_items}}
                                    <div class="mt-2 space-y-1">
                                      {{#each docSummary.result.receipt.line_items as |lineItem|}}
                                        <div
                                          class="text-xs p-2 tio-bg-surface rounded border tio-border-success"
                                        >
                                          <div class="tio-row-between mb-1">
                                            <span
                                              class="font-semibold"
                                            >{{lineItem.line_item}}</span>
                                            {{#if lineItem.line_amount}}
                                              <span class="tio-fg-success-body font-bold">
                                                {{safeFormatNumber
                                                  lineItem.line_amount
                                                  style="currency"
                                                  currency="USD"
                                                }}
                                              </span>
                                            {{/if}}
                                          </div>
                                          {{#if lineItem.line_details}}
                                            <p
                                              class="tio-fg-secondary"
                                            >{{lineItem.line_details}}</p>
                                          {{/if}}
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 tio-text-xs-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_line_items"
                                        }}</span>
                                    </div>
                                  {{/if}}
                                  {{#if docSummary.result.receipt.summary}}
                                    <button
                                      type="button"
                                      aria-expanded={{if
                                        (this.isSummaryExpanded (concat attachment.id "-receipt"))
                                        "true"
                                        "false"
                                      }}
                                      class="tio-row-tight tio-text-xs-semibold tio-fg-success-body hover:tio-fg-success-h transition-colors mt-3"
                                      {{on
                                        "click"
                                        (fn this.toggleSummary (concat attachment.id "-receipt"))
                                      }}
                                    >
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.summary"
                                        }}</span>
                                      <DisclosureToggle
                                        @expanded={{this.isSummaryExpanded
                                          (concat attachment.id "-receipt")
                                        }}
                                        @bare={{true}}
                                      />
                                    </button>
                                    {{#if
                                      (this.isSummaryExpanded (concat attachment.id "-receipt"))
                                    }}
                                      <p class="text-xs tio-fg-secondary mt-1">
                                        {{docSummary.result.receipt.summary}}
                                      </p>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{#if docSummary.result.transcript}}
                                <div class="app-status-panel app-status-panel--info mb-4 p-3">
                                  <h4 class="tio-text-sm-semibold tio-fg-secondary mb-2">{{t
                                      "common.tas.approval.drawer.attachments.analysis.course_details"
                                    }}</h4>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.institution"
                                      }}</span>
                                    {{#if docSummary.result.transcript.institution}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.institution}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.student"
                                      }}</span>
                                    {{#if docSummary.result.transcript.student_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.student_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.program"
                                      }}</span>
                                    {{#if docSummary.result.transcript.program}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.program}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  {{#if docSummary.result.transcript.courses}}
                                    {{#if docSummary.result.transcript.courses.length}}
                                      <div class="mt-2 space-y-1">
                                        <p class="tio-text-xs-semibold tio-fg-secondary mb-1">{{t
                                            "common.tas.approval.drawer.attachments.analysis.courses"
                                          }}</p>
                                        {{#each docSummary.result.transcript.courses as |course|}}
                                          <div
                                            class="text-xs p-2 tio-bg-surface rounded border app-divider-info"
                                          >
                                            <div class="tio-row-between">
                                              <span class="font-semibold">{{course.course}}</span>
                                              {{#if course.grade}}
                                                <Chip
                                                  @intent="default"
                                                  @size="sm"
                                                >{{course.grade}}</Chip>
                                              {{else}}
                                                <span
                                                  class="tio-row-extra-tight app-icon-warning font-semibold"
                                                >
                                                  <span>{{t
                                                      "common.tas.approval.drawer.attachments.analysis.grade"
                                                    }}</span>
                                                  <PhWarning @size="16" @weight="fill" />
                                                  <span>{{t
                                                      "common.tas.approval.drawer.attachments.analysis.not_found"
                                                    }}</span>
                                                </span>
                                              {{/if}}
                                            </div>
                                            {{#if course.period}}
                                              <p class="tio-text-caption mt-1">{{course.period}}</p>
                                            {{/if}}
                                            {{#if course.course_detail}}
                                              <p
                                                class="tio-fg-secondary mt-1"
                                              >{{course.course_detail}}</p>
                                            {{/if}}
                                          </div>
                                        {{/each}}
                                      </div>
                                    {{else}}
                                      <div
                                        class="tio-row-extra-tight app-icon-warning mt-2 tio-text-xs-semibold"
                                      >
                                        <PhWarning @size="16" @weight="fill" />
                                        <span>{{t
                                            "common.tas.approval.drawer.attachments.analysis.no_courses"
                                          }}</span>
                                      </div>
                                    {{/if}}
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 tio-text-xs-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_courses"
                                        }}</span>
                                    </div>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.summary}}
                                    <button
                                      type="button"
                                      aria-expanded={{if
                                        (this.isSummaryExpanded
                                          (concat attachment.id "-transcript")
                                        )
                                        "true"
                                        "false"
                                      }}
                                      class="tio-row-tight app-status-text-info tio-text-xs-semibold transition-colors mt-3"
                                      {{on
                                        "click"
                                        (fn this.toggleSummary (concat attachment.id "-transcript"))
                                      }}
                                    >
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.summary"
                                        }}</span>
                                      <DisclosureToggle
                                        @expanded={{this.isSummaryExpanded
                                          (concat attachment.id "-transcript")
                                        }}
                                        @bare={{true}}
                                      />
                                    </button>
                                    {{#if
                                      (this.isSummaryExpanded (concat attachment.id "-transcript"))
                                    }}
                                      <p class="text-xs tio-fg-secondary mt-1">
                                        {{docSummary.result.transcript.summary}}
                                      </p>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{#if docSummary.result.syllabus}}
                                <div class="app-status-panel app-status-panel--info mb-4 p-3">
                                  <h4 class="tio-text-sm-semibold tio-fg-secondary mb-2">{{t
                                      "common.tas.approval.drawer.attachments.analysis.syllabus_details"
                                    }}</h4>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.institution"
                                      }}</span>
                                    {{#if docSummary.result.syllabus.institution}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.syllabus.institution}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.course_name"
                                      }}</span>
                                    {{#if docSummary.result.syllabus.course_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.syllabus.course_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.course_dates"
                                      }}</span>
                                    {{#if
                                      (and
                                        docSummary.result.syllabus.course_start_date
                                        docSummary.result.syllabus.course_end_date
                                      )
                                    }}
                                      {{! Date-only values (YYYY-MM-DD) parse at UTC
                                      midnight, so format in UTC — otherwise an
                                      approver west of UTC reads both dates a day early. }}
                                      <span class="tio-fg-secondary">
                                        {{safeFormatDate
                                          docSummary.result.syllabus.course_start_date
                                          month="short"
                                          day="numeric"
                                          year="numeric"
                                          timeZone="UTC"
                                        }}
                                        &ndash;
                                        {{safeFormatDate
                                          docSummary.result.syllabus.course_end_date
                                          month="short"
                                          day="numeric"
                                          year="numeric"
                                          timeZone="UTC"
                                        }}
                                      </span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  {{! Textbooks — is_required is what an approver checks
                                  book costs against. }}
                                  {{#if docSummary.result.syllabus.textbooks.length}}
                                    <div class="mt-2 space-y-1">
                                      <p class="tio-text-xs-semibold tio-fg-secondary mb-1">{{t
                                          "common.tas.approval.drawer.attachments.analysis.textbooks"
                                        }}</p>
                                      {{#each docSummary.result.syllabus.textbooks as |book|}}
                                        <div
                                          class="text-xs p-2 tio-bg-surface rounded border app-divider-info"
                                        >
                                          <div class="tio-row-between">
                                            <span class="font-semibold">{{#if book.title}}
                                                {{book.title}}
                                              {{else}}
                                                <NotFound />
                                              {{/if}}</span>
                                            {{#if book.is_required}}
                                              <Chip @intent="primary" @size="sm">{{t
                                                  "common.tas.approval.drawer.attachments.analysis.item_required"
                                                }}</Chip>
                                            {{else}}
                                              <Chip @intent="default" @size="sm">{{t
                                                  "common.tas.approval.drawer.attachments.analysis.item_optional"
                                                }}</Chip>
                                            {{/if}}
                                          </div>
                                          {{#if book.author}}
                                            <p class="tio-text-caption mt-1">{{book.author}}</p>
                                          {{/if}}
                                          {{#if book.edition}}
                                            <p class="tio-fg-secondary mt-1">{{book.edition}}</p>
                                          {{/if}}
                                          {{#if book.isbn}}
                                            <p class="tio-text-caption mt-1">{{book.isbn}}</p>
                                          {{/if}}
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 tio-text-xs-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_textbooks"
                                        }}</span>
                                    </div>
                                  {{/if}}

                                  {{#if docSummary.result.syllabus.materials.length}}
                                    <div class="mt-2 space-y-1">
                                      <p class="tio-text-xs-semibold tio-fg-secondary mb-1">{{t
                                          "common.tas.approval.drawer.attachments.analysis.materials"
                                        }}</p>
                                      {{#each docSummary.result.syllabus.materials as |material|}}
                                        <div
                                          class="text-xs p-2 tio-bg-surface rounded border app-divider-info"
                                        >
                                          <div class="tio-row-between">
                                            <span class="font-semibold">{{#if material.item}}
                                                {{material.item}}
                                              {{else}}
                                                <NotFound />
                                              {{/if}}</span>
                                            {{#if material.is_required}}
                                              <Chip @intent="primary" @size="sm">{{t
                                                  "common.tas.approval.drawer.attachments.analysis.item_required"
                                                }}</Chip>
                                            {{else}}
                                              <Chip @intent="default" @size="sm">{{t
                                                  "common.tas.approval.drawer.attachments.analysis.item_optional"
                                                }}</Chip>
                                            {{/if}}
                                          </div>
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 tio-text-xs-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_materials"
                                        }}</span>
                                    </div>
                                  {{/if}}

                                  {{#if docSummary.result.syllabus.summary}}
                                    <button
                                      type="button"
                                      class="tio-row-tight app-status-text-info tio-text-xs-semibold transition-colors mt-3"
                                      aria-expanded={{if
                                        (this.isSummaryExpanded (concat attachment.id "-syllabus"))
                                        "true"
                                        "false"
                                      }}
                                      {{on
                                        "click"
                                        (fn this.toggleSummary (concat attachment.id "-syllabus"))
                                      }}
                                    >
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.summary"
                                        }}</span>
                                      <DisclosureToggle
                                        @expanded={{this.isSummaryExpanded
                                          (concat attachment.id "-syllabus")
                                        }}
                                        @bare={{true}}
                                      />
                                    </button>
                                    {{#if
                                      (this.isSummaryExpanded (concat attachment.id "-syllabus"))
                                    }}
                                      <p class="text-xs tio-fg-secondary mt-1">
                                        {{docSummary.result.syllabus.summary}}
                                      </p>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{#if docSummary.is_error}}
                                <div
                                  class="tio-row-tight p-3 tio-bg-danger-soft border tio-border-danger rounded-lg"
                                >
                                  <PhWarning @size="16" class="tio-fg-danger shrink-0" />
                                  <p class="text-sm tio-fg-danger-body">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.error_processing"
                                    }}
                                  </p>
                                </div>
                              {{/if}}
                            {{/if}}

                            {{#if docSummary.result.general.summary}}
                              <button
                                type="button"
                                aria-expanded={{if
                                  (this.isSummaryExpanded (concat attachment.id "-doc"))
                                  "true"
                                  "false"
                                }}
                                class="tio-row-tight tio-text-sm-semibold tio-fg-brand transition-colors mt-3 mb-2"
                                {{on "click" (fn this.toggleSummary (concat attachment.id "-doc"))}}
                              >
                                {{#let (this.categoryFor attachment) as |category|}}
                                  {{#if category}}
                                    <Chip @intent="default" @size="sm">{{category}}</Chip>
                                  {{else}}
                                    {{#let (this.getDocumentTypeInfo docSummary) as |typeInfo|}}
                                      <Chip
                                        @intent="default"
                                        @appearance="outlined"
                                        @size="sm"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.inferred_type_title"
                                        }}
                                      >{{typeInfo.label}}</Chip>
                                    {{/let}}
                                  {{/if}}
                                {{/let}}
                                <span>{{t
                                    "common.tas.approval.drawer.attachments.analysis.document_summary"
                                  }}</span>
                                <DisclosureToggle
                                  @expanded={{this.isSummaryExpanded (concat attachment.id "-doc")}}
                                  @bare={{true}}
                                />
                              </button>

                              {{#if (this.isSummaryExpanded (concat attachment.id "-doc"))}}
                                <p class="text-sm tio-fg-secondary leading-relaxed mb-3">
                                  {{docSummary.result.general.summary}}
                                </p>
                              {{/if}}
                            {{/if}}
                          </div>
                        {{/if}}
                      </:body>
                    </Section>
                  {{else}}
                    <Section {{this.registerCard attachment.id}}>
                      <:header>
                        <div class="tio-col-tight">
                          <div class="tio-row-tight w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="tio-col-extra-tight tio-fill-min">
                                <div class="tio-row-tight tio-fg-muted italic min-w-0">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    <FileIcon @size="24" class="shrink-0" />
                                  {{/let}}
                                  <span class="block truncate">{{attachment.filename}}</span>
                                  <span class="shrink-0 text-sm">{{t
                                      "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                    }}</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs tio-fg-disabled ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <button
                                type="button"
                                aria-expanded={{if
                                  (this.isCardExpanded attachment.id)
                                  "true"
                                  "false"
                                }}
                                class="p-1 tio-fg-disabled hover:tio-fg-secondary transition-colors shrink-0"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.toggle_details"
                                }}
                                {{on "click" (fn this.toggleCard attachment.id)}}
                              >
                                <DisclosureToggle
                                  @expanded={{this.isCardExpanded attachment.id}}
                                  @bare={{true}}
                                />
                              </button>
                            {{else}}
                              <div class="tio-col-extra-tight tio-fill-min">
                                <div class="tio-cluster-tight min-w-0 overflow-hidden">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="tio-cluster-tight min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="tio-text-caption ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="tio-row-tight shrink-0 ml-auto">
                                <AiStatusChip @v={{this.aiValidationFor attachment}} />
                                {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                                  <Chip @intent="warning" @size="sm">{{t
                                      "common.tas.approval.drawer.attachments.card_usage_receipt"
                                    }}</Chip>
                                {{/if}}
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 tio-fg-danger hover:tio-bg-danger-soft rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    aria-label={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        {{#if (this.isBodyVisible attachment)}}
                          <AiValidationPanel @v={{this.aiValidationFor attachment}} />
                          <p class="text-sm tio-fg-muted">
                            {{t "common.tas.approval.drawer.attachments.analysis.processing"}}
                          </p>
                        {{/if}}
                      </:body>
                    </Section>
                  {{/if}}
                {{/let}}
              {{else}}
                <Section {{this.registerCard attachment.id}}>
                  <:header>
                    <div class="tio-col-tight">
                      <div class="tio-row-tight w-full">
                        {{#if (@isDeleted attachment)}}
                          <div class="tio-col-extra-tight tio-fill-min">
                            <div class="tio-row-tight tio-fg-muted italic min-w-0">
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                {{#if attachment.signedUrl}}
                                  <a
                                    href={{attachment.signedUrl}}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.view_file"
                                      fileName=attachment.filename
                                    }}
                                  >
                                    <FileIcon @size="24" class="shrink-0" />
                                    <span class="block truncate">{{attachment.filename}}</span>
                                  </a>
                                {{else}}
                                  <FileIcon @size="24" class="shrink-0" />
                                  <span class="block truncate">{{attachment.filename}}</span>
                                {{/if}}
                              {{/let}}
                              <span class="shrink-0 text-sm">{{t
                                  "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                }}</span>
                            </div>
                            {{#if attachment.attachmentDeletedAt}}
                              <div class="text-xs tio-fg-disabled ml-7">
                                {{t
                                  "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                }}
                                {{safeFormatDate
                                  attachment.attachmentDeletedAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                          <button
                            type="button"
                            aria-expanded={{if (this.isCardExpanded attachment.id) "true" "false"}}
                            class="p-1 tio-fg-disabled hover:tio-fg-secondary transition-colors shrink-0"
                            title={{t
                              "common.tas.approval.drawer.attachments.analysis.toggle_details"
                            }}
                            {{on "click" (fn this.toggleCard attachment.id)}}
                          >
                            <DisclosureToggle
                              @expanded={{this.isCardExpanded attachment.id}}
                              @bare={{true}}
                            />
                          </button>
                        {{else}}
                          <div class="tio-col-extra-tight tio-fill-min">
                            <div class="tio-row-tight min-w-0 overflow-hidden">
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                {{#if attachment.signedUrl}}
                                  <a
                                    href={{attachment.signedUrl}}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.view_file"
                                      fileName=attachment.filename
                                    }}
                                  >
                                    <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                    <span
                                      class="font-semibold block truncate"
                                    >{{attachment.filename}}</span>
                                  </a>
                                {{else}}
                                  <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                  <span
                                    class="font-semibold block truncate max-w-96"
                                  >{{attachment.filename}}</span>
                                {{/if}}
                              {{/let}}
                            </div>
                            {{#if attachment.createdAt}}
                              <div class="tio-text-caption ml-7">
                                {{t
                                  "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                }}
                                {{safeFormatDate
                                  attachment.createdAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                          <div class="tio-row-tight shrink-0 ml-auto">
                            <AiStatusChip @v={{this.aiValidationFor attachment}} />
                            {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                              <Chip @intent="warning" @size="sm">{{t
                                  "common.tas.approval.drawer.attachments.card_usage_receipt"
                                }}</Chip>
                            {{/if}}
                            {{#if @deleteAttachment}}
                              <button
                                type="button"
                                class="p-2 tio-fg-danger hover:tio-bg-danger-soft rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                }}
                                aria-label={{t
                                  "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                }}
                                {{on "click" (fn this.confirmDelete attachment)}}
                              >
                                <PhTrash @size="18" />
                              </button>
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </:header>
                  <:body>
                    {{#if (this.isBodyVisible attachment)}}
                      <AiValidationPanel @v={{this.aiValidationFor attachment}} />
                      <p class="text-sm tio-fg-muted">
                        {{t "common.tas.approval.drawer.attachments.analysis.no_analysis"}}
                      </p>
                    {{/if}}
                  </:body>
                </Section>
              {{/if}}
            {{/let}}
          {{/each}}
        {{else}}
          <div class="text-center py-8">
            <PhFile @size="48" class="tio-fg-muted mx-auto mb-2" />
            <p class="text-sm tio-fg-muted">{{t
                "common.tas.approval.drawer.attachments.analysis.no_attachments"
              }}</p>
          </div>
        {{/if}}
      </div>
    </div>

    <Modal
      @isOpen={{this.showConfirmModal}}
      @onClose={{this.cancelDelete}}
      @size="sm"
      @ariaLabel={{t "common.aria_labels.delete_attachment"}}
    >
      <:header>
        <h3 class="tio-text-heading">{{t
            "common.tas.approval.drawer.attachments.analysis.delete_title"
          }}</h3>
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary">
          {{#if this.attachmentToDelete}}
            {{#if this.attachmentToDelete.filename}}
              {{t
                "common.tas.approval.drawer.attachments.analysis.confirm_delete_named"
                fileName=this.attachmentToDelete.filename
              }}
            {{else}}
              {{t "common.tas.approval.drawer.attachments.analysis.confirm_delete_generic"}}
            {{/if}}
          {{else}}
            {{t "common.tas.approval.drawer.attachments.analysis.confirm_delete_generic"}}
          {{/if}}
        </p>
      </:body>
      <:footer>
        <Footer
          @onCancel={{this.cancelDelete}}
          @onSubmit={{this.performDelete}}
          @submitText={{t "common.tas.approval.drawer.attachments.analysis.delete_action"}}
          @submitIntent="danger"
          @cancelText={{t "common.tas.approval.drawer.attachments.analysis.cancel_action"}}
          @isRunning={{@deleteAttachment.isRunning}}
        />
      </:footer>
    </Modal>
  </template>
}

export default AttachmentsAnalysisView;
